import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, IconButton, Popover, useMediaQuery, useTheme, } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { clearSearchResults } from "@application/features/globalSearch/globalSearchSlice";
import { globalSearchAsync } from "@application/features/globalSearch/globalSearchThunk";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import StyledSearchInput from "@ui/components/inputs/StyledSearchInput";
import { debounce } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import SearchResult from "./searchResult";
import { Icon } from "@iconify/react";
const GlobalSearch = ({ bordered = true, autoFocus = false, fullWidth = false, }) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const searchInputRef = useRef(null);
    const globalSearchResults = useAppSelector((state) => state.globalSearch.searchResults);
    const isSearching = useAppSelector((state) => state.globalSearch.isSearching);
    const [searchText, setSearchText] = useState("");
    const [searchAnchorEl, setSearchAnchorEl] = useState(null);
    const isIpadMini = useMediaQuery("(max-width: 768px)");
    const isBelow1200 = useMediaQuery(theme.breakpoints.down(1201));
    // Ensure only one Popover is open by resetting anchor when screen size changes
    useEffect(() => {
        setSearchAnchorEl(null);
    }, [isBelow1200]);
    const searchOpen = Boolean(searchAnchorEl) && (Boolean(globalSearchResults) || isSearching);
    const navigate = useNavigate();
    const getGlobalSearchResults = useCallback(debounce((searchText) => {
        const searchCommand = {
            searchParam: searchText,
        };
        dispatch(globalSearchAsync(searchCommand));
    }, 1000), [globalSearchAsync]);
    useEffect(() => {
        if (location.state?.manualTabChange) {
            setSearchText("");
            handleClose();
            dispatch(clearSearchResults());
        }
    }, [location.state?.manualTabChange]);
    useEffect(() => {
        dispatch(clearSearchResults());
        if (searchText && searchText.trim().length >= 2) {
            getGlobalSearchResults(searchText);
        }
    }, [searchText, getGlobalSearchResults]);
    const handleClose = () => setSearchAnchorEl(null);
    const resetGrid = () => {
        setSearchAnchorEl(null);
        // Trigger grid reset when search is cleared
        navigate(location.pathname, {
            state: {
                resetSearch: true,
            },
            replace: true,
        });
    };
    const handleEnterPress = (event) => {
        if (event.key === "Enter" && searchText.trim().length >= 2) {
            const searchCommand = {
                searchParam: searchText,
            };
            dispatch(globalSearchAsync(searchCommand));
            if (searchInputRef.current) {
                setSearchAnchorEl(searchInputRef.current);
            }
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    };
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        if (!value.trim()) {
            resetGrid();
        }
        else if (value.trim().length >= 2) {
            setSearchAnchorEl(searchInputRef.current);
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
        else {
            setSearchAnchorEl(null);
        }
    };
    const handleResetSearchAndGrid = () => {
        setSearchText("");
        resetGrid();
    };
    return (_jsxs(React.Fragment, { children: [_jsxs(Box, { className: "search-bar-container", sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 4,
                    position: "relative",
                    zIndex: 1301,
                    // borderBottom: isBelow1200 ? "1px solid rgba(0, 36, 77, .1)" : 0,
                }, children: [_jsx(StyledSearchInput, { ref: searchInputRef, onChange: handleInputChange, onKeyDown: handleEnterPress, value: searchText, autoFocus: autoFocus, bordered: bordered, fullWidth: fullWidth, handleResetCallback: handleResetSearchAndGrid }), isBelow1200 && (_jsx(Button, { variant: "text", color: "inherit", sx: { fontWeight: 500 }, onClick: handleResetSearchAndGrid, children: "Reset" }))] }), _jsx(Popover, { open: searchOpen, onClose: handleClose, anchorEl: searchAnchorEl, 
                // BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
                slotProps: { paper: { sx: { borderRadius: "20px" } } }, sx: {
                    boxShadow: theme.shadows[24],
                }, anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "center",
                }, transformOrigin: {
                    vertical: "top",
                    horizontal: "center",
                }, disablePortal: true, disableAutoFocus: true, disableEnforceFocus: true, disableRestoreFocus: true, children: _jsxs(Box, { gap: "16px", display: "flex", flexDirection: "column", sx: {
                        width: {
                            xs: "328px",
                            sm: isIpadMini ? "445px" : "596px",
                            md: "596px",
                        },
                    }, marginTop: 1, children: [isIpadMini && (_jsx(IconButton, { size: "small", sx: { position: "absolute", top: 8, right: 8 }, onClick: handleClose, children: _jsx(Icon, { icon: "eva:close-fill", width: "24", height: "24", color: theme.palette.text.disabled }) })), _jsx(SearchResult, { closePopover: handleClose, data: globalSearchResults, searchTerm: searchText, handleModuleChange: () => { }, isFetching: isSearching })] }) })] }));
};
export default GlobalSearch;
